/**
 * The case 2 demo patient — Alicia Renee Ward.
 *
 * Synthetic chart from the dev tenant. EVERYTHING HERE IS REAL, captured from
 * the live system. Nothing is authored. Where the capture gave us nothing —
 * labs, allergies, immunisations, encounters, condition onset dates — it is
 * absent rather than invented, and the surfaces that would have shown it render
 * their genuine empty states.
 *
 * Unlike case 1, the identity did not have to be derived: the tenant's own
 * patient list carries her name, date of birth and sex.
 */

import { CHART_CONDITIONS } from './chart.generated';

/** The demo's fixed "today" — the day the chart was captured. */
export const DEMO_TODAY = new Date('2026-08-24T00:00:00Z');

/** A local handle for the demo's SMART session. Not clinical data. */
export const DEMO_SESSION_ID = 'demo-session-002';

export const DEMO_TENANT_ID = 'dev-tenant';
export const DEMO_PATIENT_FHIR_ID = 'd6ca2ab3-eaf1-5002-a444-45d14d724edf';
export const DEMO_MRN = 'd6ca2ab3-eaf1-5002-a444-45d14d724edf';
export const DEMO_PATIENT_NAME = 'Alicia Renee Ward';

/** The history window the demo presents. 'all' matches the capture, which
 *  spans from her birth year to 2024 — a narrower window would hide most of
 *  it and make the chart look emptier than the record is. */
export const DEMO_HISTORY_WINDOW = 'all';

/**
 * FHIR Patient resource, as fetchSmartPatient expects (types/smart.ts).
 * Name, birth date and sex are exactly as the tenant's patient list reports
 * them. Address, telephone and managing organisation are omitted rather than
 * invented; the patient drawer shows them as absent.
 */
export const demoPatientResource = {
  resourceType: 'Patient' as const,
  id: DEMO_PATIENT_FHIR_ID,
  active: true,
  name: [{ given: ['Alicia', 'Renee'], family: 'Ward', text: DEMO_PATIENT_NAME, use: 'official' }],
  gender: 'female',
  birthDate: '1987-10-02',
  identifier: [{ system: 'urn:demo:mrn', value: DEMO_MRN, type: { text: 'MRN' } }],
};

/** REAL: both blood-pressure series, from the captured answer's own tables. */
export { SYSTOLIC_SERIES as systolicSeries, DIASTOLIC_SERIES as diastolicSeries } from './chart.generated';

/** REAL: the active problem list, from the twin's own condition families. */
export const conditions = CHART_CONDITIONS;

/**
 * REAL: her current medications, exactly as the captured reasoning answer
 * lists them — drug, instruction, most recent order date and the problem each
 * is for. The pregnancy-safety answer discusses these by name, so the list has
 * to match or the demo contradicts its own reasoning.
 */
export const medications = [
  {
    name: 'Acetaminophen 300 MG / HYDROcodone Bitartrate 5 MG Oral Tablet',
    sig: 'Take 1 tablet by mouth every 6 hours as needed for pain.',
    lastOrdered: '2026-07-24',
    indication: 'Chronic pain',
  },
  {
    name: 'Ibuprofen 600 MG Oral Tablet',
    sig: 'Take 1 tablet (600 mg) by mouth every 8 hours as needed for pain.',
    lastOrdered: '2026-05-25',
    indication: 'Chronic pain',
  },
  {
    name: 'Alprazolam 0.5 MG Oral Tablet',
    sig: 'Take 1 tablet (0.5 mg) by mouth three times daily as needed for anxiety.',
    lastOrdered: '2026-03-26',
    indication: 'Severe anxiety (panic)',
  },
  {
    name: 'topiramate 50 MG Oral Tablet',
    sig: 'Take 1 tablet (50 mg) by mouth twice daily.',
    lastOrdered: '2024-01-25',
    indication: 'Chronic intractable migraine without aura',
  },
  {
    name: 'lisinopril 10 MG Oral Tablet',
    sig: 'Take 1 tablet (10 mg) by mouth once daily.',
    lastOrdered: '2023-12-01',
    indication: 'Peripartum cardiomyopathy',
  },
  {
    name: '24 HR metoprolol succinate 50 MG Extended Release Oral Tablet',
    sig: 'Take 1 tablet (50 mg) by mouth once daily.',
    lastOrdered: '2023-12-01',
    indication: 'Peripartum cardiomyopathy',
  },
];

/** Every medication captured is a current one — the answer lists them active. */
export const activeMedications = medications;

/**
 * Resource counts for the patient drawer. Only the resource types we actually
 * hold are counted; claiming a lab or encounter total we never received would
 * be the same invention in tally form.
 */
export const resourceCounts = [
  { resourceType: 'Condition', count: conditions.length },
  { resourceType: 'MedicationRequest', count: medications.length },
];

/**
 * SmartClinicalPreview for the patient drawer. Arrays we have no captured data
 * for stay empty, so the drawer reports them as absent rather than showing
 * something that was never in the chart.
 */
export const demoClinicalPreview = {
  strategy: 'captured-from-dev',
  counts: resourceCounts,
  conditions: conditions.map((c, i) => ({
    id: `cond-${i}`,
    title: c.title,
    subtitle: c.code,
    status: c.status,
    category: 'problem-list-item',
  })),
  medications: medications.map((m, i) => ({
    id: `med-${i}`,
    title: m.name,
    subtitle: m.sig,
    date: m.lastOrdered,
    status: 'active',
  })),
  observations: [],
  encounters: [],
  allergies: [],
  procedures: [],
  reports: [],
  documents: [],
  carePlans: [],
  immunizations: [],
};
